import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import { isEqual } from 'es-toolkit';
import type { SelectedPlatformsSignal, UpdateSelectedPlatformsEvent } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

export const platformSelectionStateSchema = z.object({
    platforms: z.array(z.string()).nullable().default(null),
});

export type PlatformSelectionState = z.infer<typeof platformSelectionStateSchema>;

export type PlatformSelectionModelEventBus = EventBus<SelectedPlatformsSignal, UpdateSelectedPlatformsEvent>;

function normalizePlatforms(platformIds: string[] | null): string[] {
    return [...new Set(platformIds ?? [])].sort();
}

interface PlatformSelectionModelInit {
    persisted: PlatformSelectionState;
}

export interface PlatformSelectionModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: PlatformSelectionModelEventBus;
    readonly selectedPlatforms: ReadonlySignal<string[]>;
    setSelectedPlatforms(platformIds: string[]): boolean;
    toJSON(): PlatformSelectionState;
    markClean(): void;
}

export const PlatformSelectionModel: ModelConstructor<PlatformSelectionModelShape, [PlatformSelectionModelInit]> =
    createModel<PlatformSelectionModelShape, [PlatformSelectionModelInit]>((init: PlatformSelectionModelInit) => {
        const persistedPlatforms = signal<string[]>(normalizePlatforms(init.persisted.platforms));
        const cleanPlatforms = signal<string[]>(persistedPlatforms.value);

        const selectedPlatforms: ReadonlySignal<string[]> = computed(() => persistedPlatforms.value);
        const dirty: ReadonlySignal<boolean> = computed(() => !isEqual(persistedPlatforms.value, cleanPlatforms.value));

        const setSelectedPlatforms = (platformIds: string[]): boolean => {
            const normalized = normalizePlatforms(platformIds);
            if (isEqual(persistedPlatforms.value, normalized)) {
                return false;
            }

            persistedPlatforms.value = normalized;
            return true;
        };

        const eventBus: PlatformSelectionModelEventBus = EventBusBuilder.create()
            .addSignal('widget::selectedPlatforms', () => selectedPlatforms)
            .addEvent('widget::updateSelectedPlatforms', ({ platformIds }) => {
                setSelectedPlatforms(platformIds);
            })
            .build();

        return {
            dirty,
            eventBus,
            selectedPlatforms,
            setSelectedPlatforms,
            toJSON(): PlatformSelectionState {
                return { platforms: persistedPlatforms.value.length === 0 ? null : persistedPlatforms.value };
            },
            markClean(): void {
                cleanPlatforms.value = persistedPlatforms.value;
            },
        };
    });

export type PlatformSelectionModel = Model<PlatformSelectionModelShape>;
